import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { useSite } from "@/lib/site";
import SeoHead from "@/lib/SeoHead";
import {
  Award, Users, Globe2, Calendar, ShieldCheck, Wrench, Factory, Cog,
  Target, Eye, Heart, Flame, Hammer, Quote, ArrowRight, Sparkles,
  CheckCircle2, MapPin,
} from "lucide-react";

const ICONS = { Award, Users, Globe2, Calendar, ShieldCheck, Wrench, Factory, Cog, Target, Eye, Heart, Flame, Hammer, MapPin };

export default function About() {
  const { settings } = useSite();
  const [page, setPage] = useState(null);
  const [certs, setCerts] = useState([]);
  const [testimonial, setTestimonial] = useState(null);

  useEffect(() => {
    api.get("/pages/about").then((r) => setPage(r.data)).catch(() => setPage(null));
    api.get("/certifications").then((r) => setCerts(r.data || []));
    api.get("/testimonials").then((r) => setTestimonial((r.data || [])[0]));
  }, []);

  const c = useMemo(() => settings?.about_page || {}, [settings]);
  const stats = settings?.stats || {};
  const heroImg = c?.hero?.image || settings?.hero_slides?.[0]?.image;
  const factoryImg = settings?.hero_slides?.[1]?.image || heroImg;
  const detailImg = settings?.hero_slides?.[2]?.image || heroImg;
  const bannerImg = c?.capabilities?.banner_image || factoryImg;
  const hqImg = c?.regions?.hq_image || factoryImg;

  if (!c?.hero) {
    // Settings not loaded yet — render minimal skeleton
    return <div className="min-h-[80vh] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C1121F] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <>
      <SeoHead
        title={page?.seo_title || "About Durga Auto Industries — 25+ Years of Precision Fasteners"}
        description={page?.seo_description || "Founded in 1998 in Ludhiana, Durga Auto Industries is a leading Indian manufacturer of precision industrial fasteners — supplying automotive OEMs, EPCs and OEMs in 20+ countries."}
      />

      {/* ─── 1 · Cinematic Hero ────────────────────────────────────────── */}
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden" data-testid="about-hero">
        {heroImg && (
          <div className="absolute inset-0">
            <img src={heroImg} alt="Durga Auto Industries manufacturing" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(10,10,10,0.55),rgba(10,10,10,0.55))]" />
          </div>
        )}
        <div className="absolute top-0 left-0 w-1 h-40 bg-[#C1121F]" aria-hidden="true" />
        <div className="absolute top-1/2 right-0 w-32 h-1 bg-[#C1121F]" aria-hidden="true" />

        <div className="relative container-x py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-6 flex items-center gap-3">
              <span className="w-12 h-px bg-[#C1121F]" />
              {c.hero.overline}
            </div>

            <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              {c.hero.headline}
              <span className="block text-[#C1121F] mt-2">{c.hero.headline_accent}</span>
            </h1>

            <p className="mt-8 body-lg text-white/75 font-body max-w-2xl">{c.hero.subtitle}</p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={c.hero.cta_primary_link || "/contact"} className="btn-primary group" data-testid="about-cta-contact">
                {c.hero.cta_primary_text || "Partner with us"}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to={c.hero.cta_secondary_link || "/products"} className="btn-outline-light">
                {c.hero.cta_secondary_text || "View product catalog"}
              </Link>
            </div>
          </div>

          {c.founder_quote?.enabled !== false && (
            <div className="lg:col-span-4 lg:mt-16">
              <div className="bg-[#C1121F] text-white p-7 md:p-8 max-w-md mx-auto lg:ml-auto" data-testid="founder-quote">
                <Quote size={28} className="text-white/40" />
                <p className="mt-4 font-body italic text-sm leading-relaxed">"{c.founder_quote?.text}"</p>
                <div className="mt-5 pt-5 border-t border-white/30">
                  <div className="font-heading font-bold text-sm">{c.founder_quote?.label}</div>
                  <div className="text-xs text-white/70 mt-1 uppercase tracking-wider">{c.founder_quote?.meta}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── 2 · Stat Strip ────────────────────────────────────────────── */}
      <section className="bg-[#1C1C1C] text-white border-y border-[#333]" data-testid="about-stats">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-[#333]">
          {[
            [Calendar, stats.years || "25+", "Years in business", "Since 1998"],
            [Users, stats.clients || "500+", "Industrial clients", "Served globally"],
            [Globe2, stats.countries || "20+", "Export countries", "Across 4 continents"],
            [Award, stats.certifications || "10+", "Certifications", "ISO · IATF · OHSAS"],
          ].map(([Icon, val, label, sub], i) => (
            <div key={i} className="p-8 lg:p-10 text-center group hover:bg-[#0A0A0A] transition-colors">
              <Icon className="text-[#C1121F] mx-auto" size={24} />
              <div className="mt-4 font-heading font-bold text-4xl md:text-5xl tracking-tight">{val}</div>
              <div className="mt-2 font-heading font-semibold text-xs uppercase tracking-[0.2em] text-white">{label}</div>
              <div className="mt-1 font-body text-xs text-white/40">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 3 · The Durga Story ───────────────────────────────────────── */}
      {c.story && (
        <section className="section-y bg-white" data-testid="about-story">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="overline mb-4">{c.story.overline}</div>
              <h2 className="font-heading font-bold text-4xl md:text-5xl leading-tight text-[#1C1C1C]">
                {c.story.title_line1} <span className="text-[#C1121F]">{c.story.title_accent}</span>
              </h2>
              <p className="mt-8 body-lg text-[#4B5563] font-body">{c.story.subtitle}</p>
              {c.story.chip && (
                <div className="mt-8 inline-flex items-center gap-3 text-sm font-heading font-semibold text-[#C1121F]">
                  <Sparkles size={16} /> {c.story.chip}
                </div>
              )}
            </div>

            <div className="lg:col-span-7 space-y-6 font-body text-[#1C1C1C] leading-relaxed text-lg">
              {(c.story.paragraphs || []).map((p, i) => (
                <p key={i} className={i === 0 ? "first-letter:text-7xl first-letter:font-heading first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:text-[#C1121F]" : ""}>{p}</p>
              ))}
              {c.story.closing && (
                <p className="font-heading font-semibold text-2xl text-[#C1121F] not-italic pt-4 border-t border-[#E5E7EB]">{c.story.closing}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ─── 4 · Manufacturing Capabilities ─────────────────────────────── */}
      {c.capabilities?.items?.length > 0 && (
        <section className="section-y bg-[#F5F5F5]" data-testid="about-capabilities">
          <div className="container-x">
            <div className="max-w-3xl mb-16">
              <div className="overline mb-4">{c.capabilities.overline}</div>
              <h2 className="h-section text-[#1C1C1C]">{c.capabilities.title}</h2>
              <p className="mt-4 body-lg text-[#4B5563] font-body">{c.capabilities.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {c.capabilities.items.map((it, i) => {
                const Icon = ICONS[it.icon] || Factory;
                return (
                  <div key={i} className="bg-white p-8 border border-[#E5E7EB] hover:border-[#C1121F] transition-all hover:-translate-y-1 duration-300" data-testid={`capability-${i}`}>
                    <Icon className="text-[#C1121F]" size={26} />
                    <div className="mt-6 font-heading font-bold text-2xl md:text-3xl text-[#1C1C1C] tracking-tight">{it.big}</div>
                    <div className="mt-2 font-heading font-semibold text-sm uppercase tracking-wider text-[#1C1C1C]">{it.label}</div>
                    <div className="mt-2 font-body text-xs text-[#4B5563] leading-relaxed">{it.sub}</div>
                  </div>
                );
              })}
            </div>

            {bannerImg && (
              <div className="mt-16 relative aspect-[21/9] overflow-hidden border border-[#E5E7EB]">
                <img src={bannerImg} alt="Durga manufacturing facility" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/80 via-transparent to-transparent" />
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <div className="overline mb-2">{c.capabilities.banner_overline}</div>
                  <div className="font-heading font-bold text-2xl md:text-4xl max-w-2xl">{c.capabilities.banner_title}</div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── 5 · Timeline ─────────────────────────────────────────────── */}
      {c.timeline?.items?.length > 0 && (
        <section className="section-y bg-[#1C1C1C] text-white relative overflow-hidden" data-testid="about-timeline">
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
            {detailImg && <img src={detailImg} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="relative container-x">
            <div className="max-w-3xl mb-16">
              <div className="overline mb-4">{c.timeline.overline}</div>
              <h2 className="h-section text-white">{c.timeline.title}</h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-[#333] md:-translate-x-1/2" aria-hidden="true" />
              <div className="space-y-12">
                {c.timeline.items.map((t, i) => (
                  <div key={i} className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-12 ${i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"}`} data-testid={`timeline-${i}`}>
                    <div className="absolute left-2.5 md:left-1/2 top-2 w-3 h-3 bg-[#C1121F] md:-translate-x-1/2 ring-4 ring-[#1C1C1C]" aria-hidden="true" />
                    <div className={`md:text-right ${i % 2 === 0 ? "" : "md:text-left"}`}>
                      <div className="font-heading font-bold text-3xl md:text-4xl text-[#C1121F] tracking-tight">{t.year}</div>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-xl text-white">{t.title}</h3>
                      <p className="mt-2 font-body text-white/60 leading-relaxed">{t.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── 6 · Process ───────────────────────────────────────────────── */}
      {c.process?.items?.length > 0 && (
        <section className="section-y bg-white" data-testid="about-process">
          <div className="container-x">
            <div className="max-w-3xl mb-16">
              <div className="overline mb-4">{c.process.overline}</div>
              <h2 className="h-section text-[#1C1C1C]">{c.process.title}</h2>
              <p className="mt-4 body-lg text-[#4B5563] font-body">{c.process.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
              {c.process.items.map((p, i) => (
                <div key={i} className="bg-white p-8 hover:bg-[#F5F5F5] transition-colors" data-testid={`process-${i}`}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-heading font-bold text-5xl text-[#C1121F] tabular-nums">{p.num}</span>
                    <span className="w-12 h-px bg-[#C1121F]/40" />
                  </div>
                  <h3 className="mt-6 font-heading font-semibold text-xl text-[#1C1C1C]">{p.title}</h3>
                  <p className="mt-3 font-body text-sm text-[#4B5563] leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── 7 · Mission / Vision / Values ─────────────────────────────── */}
      {c.mvv && (
        <section className="section-y bg-[#F5F5F5]" data-testid="about-mvv">
          <div className="container-x">
            <div className="max-w-3xl mb-16">
              <div className="overline mb-4">{c.mvv.overline}</div>
              <h2 className="h-section text-[#1C1C1C]">{c.mvv.title}</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[
                [Target, "Mission", c.mvv.mission, "#C1121F"],
                [Eye, "Vision", c.mvv.vision, "#1C1C1C"],
                [Heart, "Values", c.mvv.values, "#C1121F"],
              ].map(([Icon, label, body, color], i) => (
                <div key={i} className="relative bg-white p-10 border border-[#E5E7EB] hover:border-[#C1121F] transition-all" data-testid={`mvv-${label.toLowerCase()}`}>
                  <div className="absolute top-0 left-0 w-1 h-16" style={{ backgroundColor: color }} aria-hidden="true" />
                  <Icon size={32} className="text-[#C1121F]" />
                  <div className="overline mt-6">{label}</div>
                  <h3 className="mt-2 font-heading font-bold text-2xl text-[#1C1C1C]">Our {label.toLowerCase()}</h3>
                  <p className="mt-4 font-body text-[#4B5563] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── 8 · Why Choose Durga ──────────────────────────────────────── */}
      {c.why?.items?.length > 0 && (
        <section className="section-y bg-[#1C1C1C] text-white" data-testid="about-why">
          <div className="container-x">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-16">
              <div className="lg:col-span-5">
                <div className="overline mb-4">{c.why.overline}</div>
                <h2 className="h-section text-white">{c.why.title}</h2>
              </div>
              <p className="lg:col-span-7 body-lg text-white/70 font-body">{c.why.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#333] border border-[#333]">
              {c.why.items.map((w, i) => {
                const Icon = ICONS[w.icon] || Wrench;
                return (
                  <div key={i} className="bg-[#1C1C1C] p-8 hover:bg-[#0A0A0A] transition-colors group" data-testid={`why-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#0A0A0A] border border-[#333] group-hover:border-[#C1121F] flex items-center justify-center flex-shrink-0 transition-colors">
                        <Icon size={20} className="text-[#C1121F]" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-white">{w.title}</h3>
                        <p className="mt-3 font-body text-sm text-white/60 leading-relaxed">{w.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ─── 9 · Certifications ────────────────────────────────────────── */}
      {certs.length > 0 && (
        <section className="section-y bg-white" data-testid="about-certs">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <div className="overline mb-4">Audit-ready</div>
                <h2 className="h-section text-[#1C1C1C]">Quality you can verify.</h2>
              </div>
              <Link to="/certifications" className="font-heading font-semibold text-[#C1121F] inline-flex items-center gap-2">All certifications <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {certs.slice(0, 8).map((cert) => (
                <div key={cert.id} className="bg-[#F5F5F5] p-6 border border-transparent hover:border-[#C1121F] transition-colors group">
                  <ShieldCheck className="text-[#C1121F]" size={22} />
                  <div className="mt-4 font-heading font-bold text-base text-[#1C1C1C] group-hover:text-[#C1121F] transition-colors">{cert.name}</div>
                  <div className="mt-1 font-body text-xs text-[#4B5563]">{cert.description}</div>
                  {cert.issued_by && <div className="mt-3 font-body text-[10px] text-[#A3A3A3] uppercase tracking-wider">{cert.issued_by}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── 10 · Global Footprint ─────────────────────────────────────── */}
      {c.regions?.items?.length > 0 && (
        <section className="section-y bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="about-global">
          <div className="absolute -left-24 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[#C1121F]/10 blur-3xl pointer-events-none" />
          <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="overline mb-4">{c.regions.overline}</div>
              <h2 className="h-section text-white">{c.regions.title}</h2>
              <p className="mt-6 body-lg text-white/70 font-body">{c.regions.subtitle}</p>

              <div className="mt-10 grid grid-cols-2 gap-6 max-w-lg">
                {c.regions.items.map((r, i) => (
                  <div key={i} className="border-l-2 border-[#C1121F] pl-4">
                    <div className="font-heading font-bold text-white text-sm">{r.region}</div>
                    <div className="mt-1 font-body text-xs text-white/50">{r.list}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative aspect-[4/5] bg-[#1C1C1C] border border-[#333] overflow-hidden">
              {hqImg && <img src={hqImg} alt="Durga plant" className="w-full h-full object-cover" loading="lazy" />}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <div className="flex items-center gap-2 text-white/80">
                  <MapPin size={16} className="text-[#C1121F]" />
                  <span className="font-heading font-semibold text-sm uppercase tracking-wider">{c.regions.hq_label}</span>
                </div>
                <div className="mt-2 font-heading font-bold text-2xl text-white">{c.regions.hq_title}</div>
                <div className="mt-1 font-body text-sm text-white/60">{c.regions.hq_sub}</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── 11 · Pull-quote testimonial ────────────────────────────────── */}
      {c.pullquote_enabled && testimonial && (
        <section className="section-y bg-[#C1121F] text-white" data-testid="about-pullquote">
          <div className="container-x max-w-4xl text-center">
            <Quote size={48} className="text-white/30 mx-auto" />
            <p className="mt-8 font-heading font-semibold text-2xl md:text-3xl leading-snug">"{testimonial.content}"</p>
            <div className="mt-10 inline-block border-t border-white/30 pt-5">
              <div className="font-heading font-bold text-sm uppercase tracking-wider">{testimonial.name}</div>
              <div className="mt-1 font-body text-sm text-white/80">{testimonial.role}{testimonial.company && ` · ${testimonial.company}`}</div>
            </div>
          </div>
        </section>
      )}

      {/* ─── 12 · Final CTA ────────────────────────────────────────────── */}
      {c.final_cta && (
        <section className="section-y bg-[#1C1C1C] text-white" data-testid="about-cta">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="overline mb-4">{c.final_cta.overline}</div>
              <h2 className="h-section text-white">{c.final_cta.title}</h2>
              <p className="mt-6 body-lg text-white/70 font-body max-w-2xl">{c.final_cta.subtitle}</p>
              {c.final_cta.bullets?.length > 0 && (
                <ul className="mt-8 space-y-3 max-w-xl">
                  {c.final_cta.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 font-body text-sm text-white/80">
                      <CheckCircle2 size={16} className="text-[#C1121F] flex-shrink-0 mt-0.5" /> {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="lg:col-span-5 lg:pl-12 lg:border-l lg:border-[#333]">
              <div className="font-heading font-bold text-3xl text-white mb-6">Get in touch</div>
              <Link to={c.final_cta.cta_primary_link || "/contact"} className="btn-primary w-full justify-center group" data-testid="about-final-cta">
                {c.final_cta.cta_primary_text || "Request a Quote"} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to={c.final_cta.cta_secondary_link || "/products"} className="mt-3 btn-outline-light w-full justify-center">
                {c.final_cta.cta_secondary_text || "Browse the catalog"}
              </Link>
              <div className="mt-8 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="overline mb-1">Call us</div>
                  <div className="font-body text-white">{settings?.contact_phone || "—"}</div>
                </div>
                <div>
                  <div className="overline mb-1">Email</div>
                  <div className="font-body text-white truncate">{settings?.contact_email || "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
